<template>
  <section class="home" :class="{ 'creating-note': $route.query.type }">
    <transition :name="bgTransitionName">
      <div class="background" :key="bgGradient" :style="{ backgroundImage: bgGradient}" />
    </transition>
    <DynamoLogo appear :class="[textColor]" :char-number="activeCharNumber" />
    <transition :name="noteTransitionName" :mode="noteTransitionMode">
      <Note v-if="$route.query.type || ($route.query.id && noteLoaded)" v-model="note.content" :created-at="note.createdAt" :editable="$route.query.type !== undefined || $route.query.edit === 'true'" :emotion="note.emotion" :key="note.id" :type="note.type" @date-change="handleDateChange" @emotion-change="handleEmotionChange" @fontsize-change="noteFontSize = $event"/>
      <EmptyNote v-else-if="$store.state.application.stateInitialized && typeof $route.query.id === 'undefined'" :message="message ? message : undefined" />
    </transition>
    <StickyFab :actions="fabActions" :base="fabBase" :base-action="fabBaseAction" data-html2canvas-ignore :disabled="activeCharNumber > 256 && (typeof $route.query.type === 'string' || $route.query.edit === 'true')" :expandable="fabExpandable" @collapsed="message = null" />
    <Modal class="loading-modal" :dismissable="false" :show="sharingLoading">
      <Loader :percentage="loadingPercentage" />
      <p>Rendering your note, this may take a moment.</p>
    </Modal>
    <div v-if="sharingLoading" aria-hidden="true" class="export-section" ref="exportSection" :style="{backgroundImage: bgGradient}">
      <DynamoLogo :class="[textColor]" :char-number="activeCharNumber" />
      <NoteShell>
        <p v-if="note.type === 'text' && typeof $route.query.id !== 'undefined' && noteLoaded" :class="[noteFontSize]">{{note.content}}</p>
        <ul v-if="note.type === 'bullet' && typeof $route.query.id !== 'undefined' && noteLoaded">
          <li v-for="(line, index) in note.content.split('\n')" :key="index">{{line}}</li>
        </ul>
        <footer>
          <span class="date">{{ currentDate }}</span>
          <Emotion v-if="note.emotion" :emotion="note.emotion" />
        </footer>
      </NoteShell>
      <h1 class="footer-text" :class="[textColor]">256c.amxmln.com</h1>
    </div>
  </section>
</template>

<script>
import html2canvas from 'html2canvas';
import { Date as SugarDate } from 'sugar-date';

import DynamoLogo from '@/components/DynamoLogo.vue';
import EmptyNote from '@/components/EmptyNote.vue';
import Emotion from '@/components/Emotion.vue';
import Loader from '@/components/Loader.vue';
import Modal from '@/components/Modal.vue';
import Note from '@/components/Note.vue';
import NoteShell from '@/components/NoteShell.vue';
import StickyFab from '@/components/StickyFab.vue';

import db from '@/db';

export default {
  beforeDestroy() {
    window.removeEventListener('touchstart', this.handleSwipeStart);
    window.removeEventListener('mousedown', this.handleSwipeStart);
  },
  async beforeRouteEnter(to, from, next) {
    if (from.name === 'overview' && to.query.id) {
      try {
        const note = await db.notes.get(parseInt(to.query.id, 10));
        if (!note) next('/404');
        else {
          next((vm) => {
            /* eslint-disable no-param-reassign */
            vm.note = note;
            vm.noteLoaded = true;
            vm.noteTransitionName = 'overview';
            vm.bgTransitionName = 'fade';
            vm.fabBase = 'more';
            vm.fabBaseAction = null;
            vm.fabExpandable = true;
            vm.fabActions = [{ action: vm.editNote, icon: 'edit' }, { action: vm.shareNote, icon: 'share-alt-2' }, { action: vm.deleteNote, icon: 'delete' }];
            /* eslint-enable no-param-reassign */
          });
        }
      } catch (err) {
        this.$router.push('/');
        this.$store.commit('addToast', { message: `Sorry, something went wrong: ${err.message}` });
      }
    } else next();
  },
  components: {
    DynamoLogo,
    EmptyNote,
    Emotion,
    Loader,
    Modal,
    Note,
    NoteShell,
    StickyFab,
  },
  computed: {
    activeCharNumber() {
      if (this.contentLength > 0) return this.contentLength;
      if (this.charNumber !== null) return this.charNumber;
      return undefined;
    },
    bgGradient() {
      if (this.note.emotion) {
        const c1 = this.$store.state.application.emotions[this.note.emotion][0];
        const c2 = this.$store.state.application.emotions[this.note.emotion][1];
        return `linear-gradient(${c1}, ${c2})`;
      }
      return '';
    },
    contentLength() {
      if (this.note.content) return this.note.content.length;
      return 0;
    },
    currentDate() {
      return new SugarDate(this.note.createdAt).format('{HH}:{mm}, {dd}. {Month} {yyyy}');
    },
    textColor() {
      // calculate text color based on background
      // adapted from https://awik.io/determine-color-bright-dark-using-javascript/
      let color;
      if (this.note.emotion) [color] = this.$store.state.application.emotions[this.note.emotion];
      else color = '#555D6C';
      if (color.length === 4) color = `#${color[1]}${color[1]}${color[2]}${color[2]}${color[3]}${color[3]}`; // eslint-disable-line no-param-reassign

      const r = parseInt(color.slice(1, 3), 16);
      const g = parseInt(color.slice(3, 5), 16);
      const b = parseInt(color.slice(5, 7), 16);

      const hsp = Math.sqrt(0.299 * (r * r) + 0.587 * (g * g) + 0.114 * (b * b));

      if (hsp > 200) return 'dark';
      return 'light';
    },
    noteTransitionMode() {
      if (this.noteTransitionName === 'new-note') return 'out-in';
      return '';
    },
  },
  async created() {
    if (this.$route.query.error) this.handleError(this.$route.query.error);
    if (this.$route.query.type) {
      this.handleNewNote();
      this.$set(this.note, 'createdAt', Date.now());
      this.$set(this.note, 'type', this.$route.query.type);
    }
    if (this.$route.query.id) {
      await this.handleExistingNote();
      if (this.$route.query.edit) this.handleEditNote();
    }

    window.addEventListener('touchstart', this.handleSwipeStart);
    window.addEventListener('mousedown', this.handleSwipeStart);
  },
  data() {
    return {
      bgTransitionName: 'bg',
      charNumber: null,
      fabActions: [{ action: this.addTextNote, icon: 'text' }, { action: this.addBulletNote, icon: 'bullets' }],
      fabBase: 'add',
      fabBaseAction: this.askMessageType,
      fabExpandable: true,
      isPotentialScroll: false,
      loadingPercentage: 0,
      message: null,
      note: {
        content: null,
        createdAt: null,
        emotion: null,
        id: null,
        type: null,
      },
      noteFontSize: 'large',
      noteLoaded: false,
      noteTransitionName: 'new-note',
      originalContent: null,
      sharingLoading: false,
      swipeStart: { x: 0, y: 0 },
    };
  },
  methods: {
    async actuallyDeleteNote(id) {
      try {
        await db.notes.delete(id);
      } catch (err) {
        this.$store.commit('addToast', { message: `Could not delete note: ${err.message}` });
      }
    },
    addBulletNote() {
      this.noteTransitionName = 'new-note';
      this.$set(this.note, 'id', null);
      this.$set(this.note, 'createdAt', Date.now());
      this.$set(this.note, 'type', 'bullet');
      this.$router.push({ query: { type: 'bullet' } });
    },
    askMessageType() {
      this.message = 'What type of note would you like to write?';
    },
    addTextNote() {
      this.noteTransitionName = 'new-note';
      this.$set(this.note, 'id', null);
      this.$set(this.note, 'createdAt', Date.now());
      this.$set(this.note, 'type', 'text');
      this.$router.push({ query: { type: 'text' } });
    },
    canvasToBlob(canvas) {
      return new Promise((resolve) => {
        canvas.toBlob((blob) => resolve(blob), 'image/png');
      });
    },
    clearNote() {
      this.$set(this.note, 'createdAt', null);
      this.$set(this.note, 'content', null);
      this.$set(this.note, 'emotion', null);
      this.$set(this.note, 'type', null);
    },
    async deleteNote() {
      const oldId = parseInt(this.$route.query.id, 10);
      const timeout = 10000;
      const previousRecord = await db.notes.where('createdAt').below(this.note.createdAt).and((note) => !note.deleted).limit(1)
        .last();
      const timeoutId = window.setTimeout(() => this.actuallyDeleteNote(oldId), timeout + 100);
      await db.notes.update(oldId, { deleted: true }); // soft delete so it can be hidden
      this.$store.commit('addToast', {
        async action() {
          window.clearTimeout(timeoutId);
          await db.notes.update(oldId, { deleted: false });
          this.noteTransitionName = 'next-note';
          this.bgTransitionName = 'bg-backward';
          await this.$nextTick();
          this.$router.replace({ path: '/', query: { id: oldId } });
        },
        actionLabel: 'undo',
        message: 'Your note was deleted',
        timeout,
      });
      if (previousRecord) {
        this.noteTransitionName = 'previous-note';
        this.bgTransitionName = 'bg';
        await this.$nextTick();
        this.$router.replace({ query: { id: previousRecord.id } });
      } else {
        this.noteTransitionName = 'next-note';
        this.bgTransitionName = 'bg-backward';
        await this.$nextTick();
        this.$router.replace('/');
      }
    },
    editNote() {
      this.$router.push({ query: { edit: true, id: this.$route.query.id } });
    },
    handleDateChange(date) {
      this.$set(this.note, 'createdAt', date);
    },
    handleEditNote() {
      this.originalContent = this.note.content;
      this.fabBase = 'save';
      this.fabActions = [{ action: () => window.setTimeout(() => { this.$set(this.note, 'content', this.originalContent); this.originalContent = null; this.$router.replace({ query: { id: this.$route.query.id } }); }, 500), icon: 'cross' }, { action: () => window.setTimeout(this.saveNote, 500), icon: 'check' }];
    },
    handleEmotionChange(emotion) {
      this.$set(this.note, 'emotion', emotion);
    },
    handleError(error) {
      this.charNumber = Number.parseInt(error, 10);
      this.message = 'I’m sorry, but whatever you were looking for isn’t here or doesn’t exist.';

      this.fabExpandable = false;
      this.fabBase = 'chevron-left';
      this.fabBaseAction = this.navigateHome;
    },
    async handleExistingNote() {
      try {
        const note = await db.notes.get(parseInt(this.$route.query.id, 10));
        if (!note) this.$router.push('/404');
        else {
          this.note = note;
          this.noteLoaded = true;
          if (this.$route.query.edit) this.fabBase = 'save';
          else this.fabBase = 'more';
          this.fabBaseAction = null;
          this.fabExpandable = true;
          this.fabActions = [{ action: this.editNote, icon: 'edit' }, { action: this.shareNote, icon: 'share-alt-2' }, { action: this.deleteNote, icon: 'delete' }];
          this.setThemeColor(this.note.emotion ? this.$store.state.application.emotions[this.note.emotion][0] : null);
        }
      } catch (err) {
        this.$router.push('/');
        this.$store.commit('addToast', { message: `Sorry, something went wrong: ${err.message}` });
      }
    },
    handleExistingNoteNoReload() {
      if (this.$route.query.edit) this.fabBase = 'save';
      else this.fabBase = 'more';
      this.fabBaseAction = null;
      this.fabExpandable = true;
      this.fabActions = [{ action: this.editNote, icon: 'edit' }, { action: this.shareNote, icon: 'share-alt-2' }, { action: this.deleteNote, icon: 'delete' }];
    },
    handleNewNote() {
      this.charNumber = 0;
      this.fabExpandable = false;
      if (!this.note.content) this.fabBase = 'cross';
      else this.fabBase = 'check';
      this.fabBaseAction = this.saveNote;
      this.setThemeColor();
    },
    async handleSwipeEnd(e) {
      if (this.$route.query.type || this.$route.query.edit) return; // no gestures while creating or editing a note

      if (e.type === 'touchend') window.removeEventListener('touchend', this.handleSwipeEnd);
      if (e.type === 'mouseup') window.removeEventListener('mouseup', this.handleSwipeEnd);
      const swipeEndX = e.type === 'touchend' ? e.changedTouches[0].screenX : e.clientX;
      const swipeEndY = e.type === 'touchend' ? e.changedTouches[0].screenY : e.clientY;
      const threshold = 50;
      const thresholdY = 120;
      const distance = swipeEndX - this.swipeStart.x;
      const distanceY = swipeEndY - this.swipeStart.y;

      if (Math.abs(distance) < Math.abs(distanceY)) {
        if (this.isPotentialScroll) return;
        if (distanceY > 0) return; // it’s a scroll
        if (distanceY < thresholdY * -1) {
          this.$router.push('/overview');
          return;
        }
      }
      if (distance > threshold) {
        // swipe right, go into the past
        this.noteTransitionName = 'previous-note';
        this.bgTransitionName = 'bg';
        let previousRecord;
        if (!this.$route.query.id) previousRecord = await db.notes.orderBy('createdAt').filter((note) => !note.deleted).last();
        else {
          previousRecord = await db.notes.where('createdAt').below(this.note.createdAt).filter((note) => !note.deleted).limit(1)
            .last();
        }
        if (previousRecord) {
          this.$router.replace({ query: { id: previousRecord.id } });
          this.note = previousRecord;
          this.setThemeColor(previousRecord.emotion ? this.$store.state.application.emotions[previousRecord.emotion][0] : null);
          this.noteLoaded = true;
        } else this.$store.commit('addToast', { message: 'There’s no more notes to go back to' });
      } else if (Math.sign(distance) < 0 && distance < -threshold) {
        // swipe left, go into the future
        this.noteTransitionName = 'next-note';
        this.bgTransitionName = 'bg-backward';
        if (!this.$route.query.id) return; // there’s nothing to go to
        const nextRecord = await db.notes.where('createdAt').above(this.note.createdAt).filter((note) => !note.deleted).limit(1)
          .first();
        if (nextRecord) {
          this.$router.replace({ query: { id: nextRecord.id } });
          this.note = nextRecord;
          this.setThemeColor(nextRecord.emotion ? this.$store.state.application.emotions[nextRecord.emotion][0] : null);
          this.noteLoaded = true;
        } else this.$router.push('/');
      }
    },
    handleSwipeStart(e) {
      if (this.sharingLoading || this.$route.query.showChangelog) return; // don’t allow gestures when exporting
      const list = this.$el.querySelector('.auto-list');
      if (this.note.type === 'bullet' && this.$el.querySelector('.note').contains(e.target) && list.scrollTop !== (list.scrollHeight - list.offsetHeight)) this.isPotentialScroll = true; // we are scrolling inside the list
      else if (this.isPotentialScroll) this.isPotentialScroll = false;
      this.swipeStart.x = e.type === 'touchstart' ? e.changedTouches[0].screenX : e.clientX;
      this.swipeStart.y = e.type === 'touchstart' ? e.changedTouches[0].screenY : e.clientY;
      if (e.type === 'touchstart') window.addEventListener('touchend', this.handleSwipeEnd);
      if (e.type === 'mousedown') window.addEventListener('mouseup', this.handleSwipeEnd);
    },
    navigateHome() {
      this.$router.replace('/');
    },
    async saveNote() {
      if (this.contentLength === 0) {
        this.$router.replace('/');
        return;
      }
      try {
        const note = { ...this.note };
        if (this.note.id === null) delete note.id;
        const id = await db.notes.put(note);
        this.$router.replace({ path: '/', query: { id } });
      } catch (err) {
        this.$store.commit('addToast', { message: `Something went wrong while saving your note: ${err.message}` });
      }
    },
    setThemeColor(color) {
      document.querySelector('meta[name=theme-color]').setAttribute('content', color || '#555D6C');
    },
    downloadNote(image, filename) {
      const tempLink = document.createElement('a');
      tempLink.style.display = 'none';
      tempLink.href = image || this.downloadData.image;
      tempLink.setAttribute('download', filename || this.downloadData.filename);
      if (typeof tempLink.download === 'undefined') tempLink.setAttribute('target', '_blank');
      document.body.appendChild(tempLink);
      tempLink.click();
      document.body.removeChild(tempLink);
    },
    async shareNote() {
      if (!navigator.onLine) {
        this.$store.commit('addToast', { message: 'It appears as if you’re offline. Unfortunately, sharing a note requires a network connection at the moment. Please try again later.', timeout: 10000 });
        return;
      }
      this.sharingLoading = true;
      let interval = window.setInterval(() => {
        this.loadingPercentage += Math.min(Math.round(Math.random() * 11), 10);
        if (this.loadingPercentage >= 100) {
          window.clearInterval(interval);
          interval = null;
          this.loadingPercentage = 100;
        }
      }, 500);

      await this.$nextTick();

      try {
        const canvas = await html2canvas(this.$refs.exportSection, { letterRendering: true, scale: 2.0 });
        const image = canvas.toDataURL('image/png');
        const filename = `256c-note-${new SugarDate().format('{year}-{MM}-{dd}_{hh}-{mm}')}.png`;

        // Use sharing directly if available, else download: https://developers.google.com/web/updates/2019/05/web-share-files
        // Can’t get it to work because of Permission Denied Error, deactivated for now
        // if (window.navigator.canShare) {
        //   const blob = await this.canvasToBlob(canvas);
        //   const file = new File([blob], filename, { lastModified: Date.now() });
        //   const files = [file];
        //   Object.freeze(files);
        //   const shareData = { files };
        //   const shareable = window.navigator.canShare(shareData);
        //   if (shareable) {
        //     await window.navigator.share(shareData);
        //   } else {
        //     this.$store.commit('addToast', { message: 'Unable to share note directly, will download instead' });
        //     this.downloadNote(image, filename);
        //   }
        // } else {
        //   this.$store.commit('addToast', { message: 'Sharing directly is not supported on your device, the image will be downloaded instead' });
        //   this.downloadNote(image, filename);
        // }

        this.downloadNote(image, filename);
      } catch (err) {
        this.$store.commit('addToast', { message: `Could not render note: ${err.message}` });
      } finally {
        if (interval) window.clearInterval(interval);
        this.sharingLoading = false;
        this.loadingPercentage = 0;
      }
    },
  },
  name: 'home',
  watch: {
    async $route(newVal) {
      if (newVal.query.error) this.handleError(newVal.query.error);
      else if (newVal.query.type === 'text' || newVal.query.type === 'bullet') {
        this.handleNewNote();
      } else if (newVal.query.id) {
        if (Number.parseInt(newVal.query.id, 10) !== this.note.id) this.noteLoaded = false;
        if (!this.noteLoaded) await this.handleExistingNote();
        else this.handleExistingNoteNoReload();
        if (newVal.query.edit) window.setTimeout(this.handleEditNote, 500); // so the fab can retract
      } else {
        this.charNumber = null;
        this.message = null;
        this.fabBase = 'add';
        this.fabBaseAction = this.askMessageType;
        this.fabExpandable = true;
        this.fabActions = [{ action: this.addTextNote, icon: 'text' }, { action: this.addBulletNote, icon: 'bullets' }];
        this.clearNote();
        this.setThemeColor();
        this.noteLoaded = false;
      }
    },
    contentLength(newVal) {
      if (this.$route.query.type) {
        if (newVal === 0) this.fabBase = 'cross';
        else this.fabBase = 'check';
      }
    },
  },
};
</script>

<style lang="stylus" scoped>
@require '../styles/colors'

$transition-duration = 350ms

.home
  padding: 48px 32px 32px 32px
  height: 100%

  &.creating-note
    overflow-y: auto
    overflow-x: hidden

  &.fade-leave-active .note
    transform-origin: top center
    position: absolute
    left: 50% // to have it centered despite position: absolute
    margin-left: -147px // to have it centered despite position: absolute, the value is the note width (294) devided by two times -1
    transition: transform $transition-duration ease

  &.fade-leave-to .note
    transform: scale(0.2) translateY(-64px)

  .note,
  .empty-note
    will-change: transform, opacity
    backface-visibility: hidden
    hyphens: manual

  .background
    position: fixed
    top: 0
    left: @top
    right: @top
    bottom: @top
    z-index: 0
    will-change: transform

    &.bg-enter-active,
    &.bg-leave-active,
    &.bg-backward-enter-active,
    &.bg-backward-leave-active
      transition: transform 400ms ease
      // transition: opacity 400ms ease
      backface-visibility: hidden
      transform-style: preserve-3d

      &.bg-enter
      &.bg-backward-leave-to
        transform: translateX(-100%)
        // opacity: 0

      &.bg-leave-to
      &.bg-backward-enter
        transform: translateX(100%)
        // opacity: 0

  .dynamo-logo
    margin-bottom: 48px
    z-index: 1
    position: relative

    @media screen and (min-height: 640px)
      margin-bottom: calc(50vh - 274px) // 162 is half the note plus 112 is the distance from the bottom of the logo to the top

    &.dark
      color: alpha($black, 0.5)

  .sticky-fab
    position: fixed
    bottom: 32px
    width: calc(100% - 64px)
    max-width: 576px
    margin: 0 auto

  .modal.loading-modal
      >>> h1,
      >>> h2
        margin: 0

      >>> h1
        font-size: 2.5rem

      >>> h2
        margin-top: -10px

      p
        text-align: center
        font-size: 0.875rem

  .export-section
    width: 540px
    height: @width
    position: fixed
    top: -540px
    left: @top
    display: flex
    flex-direction: column
    justify-content: space-between
    padding: 16px
    background-image: $default-gradient
    text-shadow: none

    .dynamo-logo
      margin-bottom: 0
      transition: none

      >>> h2
        margin-top: -14px

    .note-shell
      flex-shrink: 0

      >>> .content
        display: flex
        flex-direction: column

        p
          margin: 0
          font-size: 2rem
          overflow-wrap: break-word
          word-wrap: break-word
          word-break: break-all
          word-break: break-word
          hyphens: auto
          height: calc(100% - 28px)
          font-weight: 600
          line-height: 1.35
          text-shadow: none

          &.medium
            font-size: 1.5rem

          &.small
            font-size: 1.1rem

          &.extra-small
            font-size: 0.84rem

        ul
          margin: 0
          padding-left: 1.2rem
          font-size: 1.1rem
          overflow: auto
          text-shadow: none
          list-style: none

          li
            margin-bottom: 0.5rem
            position: relative

            &::before
              content: ''
              display: block
              background-color: $black
              position: absolute
              left: -1rem
              top: 11px
              width: 6px
              height: @width
              border-radius: (@width / 2)px

        footer
          margin-top: auto
          height: 20px
          font-weight: normal
          font-size: 0.75rem
          display: flex
          align-items: center
          justify-content: space-between
          position: relative

          .date
            color: alpha($black, 0.54)
            text-shadow: none
            flex-shrink: 0

          .emotion
            flex-shrink: 0
            width: 20px
            height: 20px
            display: flex
            justify-content: center
            align-items: center
            overflow: hidden
            flex-grow: 0

    h1.footer-text
      text-align: center
      color: alpha($text, 0.5)
      margin: 0
      font-size: 1rem

      &.dark
        color: alpha($black, 0.3)

  .new-note-enter-active
    transition: transform $transition-duration ease, opacity $transition-duration ease

    &.new-note-enter
      // transform: translateX(-32px) rotate(12deg)
      transform: scale(0.8)
      opacity: 0

  .new-note-leave-active
    position: absolute
    transform-origin: top right
    transition: transform $transition-duration ease, opacity $transition-duration ease

    &.new-note-leave-to
      // transform: translateX(32px) rotate(-12deg)
      opacity: 0

  .next-note-enter-active,
  .next-note-leave-active,
  .previous-note-enter-active,
  .previous-note-leave-active
    transition: transform $transition-duration ease, opacity $transition-duration ease
    position: absolute
    left: 50% // to have it centered despite position: absolute
    margin-left: -147px // to have it centered despite position: absolute, the value is the note width (294) devided by two times -1

    &.next-note-enter
      transform: translateX(100%) scale(0.8)
      opacity: 0

    &.next-note-leave-to
      transform: translateX(-100%) scale(0.8)
      opacity: 0

    &.previous-note-enter
      transform: translateX(-100%) scale(0.8)
      opacity: 0

    &.previous-note-leave-to
      transform: translateX(100%) scale(0.8)
      opacity: 0

  .overview-enter-active,
  .overview-leave-active
    transform-origin: top center
    position: absolute
    left: 50% // to have it centered despite position: absolute
    margin-left: -147px // to have it centered despite position: absolute, the value is the note width (294) devided by two times -1
    transition: transform $transition-duration ease

    &.overview-enter
      transform: scale(0.2) translateY(-64px)

    &.overview-leave-to
      opacity: 0
</style>
